/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

@Directive({ selector: '[caTruncate]' })
export class TruncateDirective implements AfterViewInit, OnDestroy, OnChanges {
    private maxWidth: number = 275;
    private resizeObserver: ResizeObserver | null = null;

    constructor(private el: ElementRef) {}

    @Input() set caTruncate(maxWidth: number) {
        this.maxWidth = maxWidth || 275; // Fallback to default if an invalid value is provided
        this.applyTruncate();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.applyTruncateDebounced();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caTruncate'] && !changes['caTruncate'].isFirstChange()) {
            this.applyTruncate();
        }
    }

    private calculateAvailableWidth(): number {
        const nativeElement = this.el.nativeElement;
        const parentElement = nativeElement.parentElement;

        if (!parentElement) {
            console.warn('TruncateDirective: Parent element not found.');
            return this.maxWidth; // Return maxWidth if no parent element exists
        }

        const parentWidth = parentElement.getBoundingClientRect().width;

        let usedWidth = 0;
        Array.from(parentElement.children).forEach((child: HTMLElement): void => {
            if (child !== nativeElement) {
                const childStyle = getComputedStyle(child);
                if (childStyle.position !== 'absolute' && childStyle.display !== 'none') {
                    usedWidth += child.getBoundingClientRect().width;
                }
            }
        });

        const availableWidth = Math.max(this.maxWidth, parentWidth - usedWidth);

        if (availableWidth < 0) {
            console.warn('TruncateDirective: Negative width calculated. Defaulting to maxWidth.');
            return this.maxWidth; // Avoid negative values
        }

        return availableWidth;
    }

    private applyTruncate(): void {
        const nativeElement = this.el.nativeElement;
        const calculatedWidth = this.calculateAvailableWidth();

        nativeElement.style.maxWidth = `${calculatedWidth}px`;
        nativeElement.style.overflow = 'hidden'; // Ensure text does not overflow
        nativeElement.style.textOverflow = 'ellipsis'; // Apply truncation style
        nativeElement.style.whiteSpace = 'nowrap'; // Prevent wrapping
    }

    private debounce(func: Function, wait: number) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let timeout: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), wait);
        };
    }

    private applyTruncateDebounced = this.debounce(this.applyTruncate.bind(this), 100);

    ngAfterViewInit(): void {
        if (!ResizeObserver) {
            console.warn('TruncateDirective: ResizeObserver is not supported in this environment.');
            return;
        }

        this.resizeObserver = new ResizeObserver((): void => {
            this.applyTruncateDebounced();
        });

        const nativeElement = this.el.nativeElement;
        const parentElement = nativeElement.parentElement;

        if (nativeElement) {
            this.resizeObserver.observe(nativeElement);
        }
        if (parentElement) {
            this.resizeObserver.observe(parentElement);
        }

        this.applyTruncate();
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
}
